import { put, select, takeEvery } from 'redux-saga/effects';
import { Store } from '@reduxjs/toolkit';
import { DisplaySettings, GraphicMode, PDFTronTools, StoreState } from 'types';
import app from './app';
import inspection from '../inspection/inspection';
import { sanitizeDisplaySettings } from './displaySettings';

export interface AppDisplaySnapshot {
  showTextHighlights: boolean;
  showDifferenceBoxes: boolean;
  hideOtherDifferences: boolean;
  showOverlay: boolean;
  grayscaleMode: boolean;
  singlePageView: boolean;
  showRepeats: boolean;
  graphicMode: GraphicMode;
  lastGraphicMode: 'flashing' | 'transparentOverlay';
  transparencyOpacity: number;
}

/** Live viewer value. An empty mode means the graphic effect is off. */
export function liveGraphicMode(settings: {
  graphicEffectOn: boolean;
  graphicMode: 'flashing' | 'transparentOverlay';
}): GraphicMode {
  return settings.graphicEffectOn ? settings.graphicMode : '';
}

/**
 * Actions that bring the app slice in line with saved display settings.
 * Skips any flag the store already holds, so a session fetch does not redraw an open document.
 */
export function appDisplayActions(saved: Partial<DisplaySettings> | undefined, current: AppDisplaySnapshot) {
  const settings = sanitizeDisplaySettings(saved);
  const actions = [];

  if (current.showTextHighlights !== settings.showTextHighlights) {
    actions.push(app.actions.setShowTextHighlights(settings.showTextHighlights));
  }
  if (current.showDifferenceBoxes !== settings.showDifferenceBoxes) {
    actions.push(app.actions.setShowDifferenceBoxes(settings.showDifferenceBoxes));
  }
  if (current.hideOtherDifferences !== settings.hideOtherDifferences) {
    actions.push(app.actions.setHideOtherDifferences(settings.hideOtherDifferences));
  }
  if (current.showOverlay !== settings.showOverlay) {
    actions.push(app.actions.setShowOverlay(settings.showOverlay));
  }
  if (current.grayscaleMode !== settings.grayscaleMode) {
    actions.push(app.actions.setGrayscaleMode(settings.grayscaleMode));
  }
  if (current.singlePageView !== settings.singlePageView) {
    actions.push(app.actions.setSinglePageView(settings.singlePageView));
  }
  if (current.showRepeats !== settings.showRepeats) {
    actions.push(app.actions.setShowRepeats(settings.showRepeats));
  }

  const graphicMode = liveGraphicMode(settings);
  if (current.graphicMode !== graphicMode) {
    actions.push(app.actions.setGraphicMode(graphicMode));
  }
  if (current.lastGraphicMode !== settings.graphicMode) {
    actions.push(app.actions.setLastGraphicMode(settings.graphicMode));
  }
  if (current.transparencyOpacity !== settings.transparencyOpacity) {
    actions.push(app.actions.setTransparencyOpacity(settings.transparencyOpacity));
  }

  return actions;
}

/** Annotation visibility lives on the inspection slice, so only an inspection reset restores it. */
export function annotationVisibilityAction(saved: Partial<DisplaySettings> | undefined, current: boolean) {
  const settings = sanitizeDisplaySettings(saved);
  if (current === settings.showAnnotations) return null;
  return inspection.actions.setDefaultInternalAnnotationsVisibility(settings.showAnnotations);
}

/** First load: neither slice has been reset, and both still hold the session defaults. */
export function applyLoadedDisplaySettings(store: Store, saved: Partial<DisplaySettings> | undefined): void {
  const state = store.getState() as StoreState;
  // Re-inspect forces this flag off. A load that arrives during the tool must not turn it back on.
  const duringResync = state.inspection.selectedTool === PDFTronTools.RESYNC;
  appDisplayActions(saved, snapshot(state))
    .filter((action) => !(duringResync && action.type === app.actions.setHideOtherDifferences.type))
    .forEach((action) => store.dispatch(action));
  const annotation = annotationVisibilityAction(saved, state.inspection.internalAnnotationsVisibility);
  if (annotation) store.dispatch(annotation);
}

export function* applyAppDisplaySettingsAfterReset() {
  const state: StoreState = yield select();
  const actions = appDisplayActions(state.app.savedDisplaySettings, snapshot(state));
  for (const action of actions) {
    yield put(action);
  }
}

export function* applyAnnotationVisibilityAfterReset() {
  const state: StoreState = yield select();
  const action = annotationVisibilityAction(
    state.app.savedDisplaySettings,
    state.inspection.internalAnnotationsVisibility,
  );
  if (action) yield put(action);
}

function snapshot(state: StoreState): AppDisplaySnapshot {
  return {
    showTextHighlights: state.app.showTextHighlights,
    showDifferenceBoxes: state.app.showDifferenceBoxes,
    hideOtherDifferences: state.app.hideOtherDifferences,
    showOverlay: state.app.showOverlay,
    grayscaleMode: state.app.grayscaleMode,
    singlePageView: state.app.singlePageView,
    showRepeats: state.app.showRepeats,
    graphicMode: state.app.graphicMode,
    lastGraphicMode: state.app.lastGraphicMode,
    transparencyOpacity: state.app.transparencyOpacity,
  };
}

export default [
  takeEvery(app.actions.resetStore.type, applyAppDisplaySettingsAfterReset),
  takeEvery(inspection.actions.resetStore.type, applyAnnotationVisibilityAfterReset),
];
